import { HttpClient, HttpHeaders, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import _ from 'lodash';
import { Observable } from 'rxjs';
import { catchError, map, publishLast, refCount } from 'rxjs/operators';
import { InjectorModule } from 'src/app/shared/modules/injector.module';
import { AppConfigService } from 'src/app/shared/services/app-config.service';
import {
  ErrorHandler,
  ErrorHandlerFactory
} from 'src/app/shared/services/error-handler-factory.service';
import { SnackBarService } from 'src/app/shared/services/snackbar.service';
import { HttpGetOptions } from 'src/app/shared/types/http-get-options.type';

@Injectable({
  providedIn: 'root'
})
export abstract class PortalBaseService {
  private snackbarService: SnackBarService;
  protected appConfigService: AppConfigService;
  private http: HttpClient;

  private handleError: ErrorHandler;
  constructor() {
    this.snackbarService = InjectorModule.injector.get(SnackBarService);
    this.appConfigService = InjectorModule.injector.get(AppConfigService);
    this.http = InjectorModule.injector.get(HttpClient);

    const errorHandlerFactory = InjectorModule.injector.get(ErrorHandlerFactory);
    this.handleError = errorHandlerFactory.buildHandler();
  }

  public get<TResult = any>(path: string, opts?: HttpGetOptions): Observable<TResult>;

  public get<TResult = any>(
    path: string,
    ok404?: boolean,
    rethrowError?: boolean
  ): Observable<TResult>;

  public get<TResult = any>(
    path: string,
    optsOrFlag?: HttpGetOptions | boolean,
    rethrowError: boolean = false
  ): Observable<TResult> {
    const url = this.appConfigService.baseApiUrl.concat(path);

    const opts = typeof optsOrFlag === 'object' ? optsOrFlag : undefined;
    const ok404 = typeof optsOrFlag === 'boolean' ? optsOrFlag : false;

    return this.request('GET', url, null, opts, ok404, rethrowError);
  }

  public patch(
    path: string,
    body: any = undefined,
    rethrowError: boolean = false,
    options?: object
  ) {
    const url = this.appConfigService.baseApiUrl.concat(path);
    const requestOptions = options ?? this.defaultRequestOptions();

    return this.request('PATCH', url, body, requestOptions, true, rethrowError);
  }

  public post(path: string, body: any, options?: object, rethrowError = false) {
    const url = this.appConfigService.baseApiUrl.concat(path);
    options = options || this.defaultRequestOptions();

    return this.request('POST', url, body, options, true, rethrowError);
  }

  public put(path: string, body?: string, options?: object, rethrowError: boolean = false) {
    const url = this.appConfigService.baseApiUrl.concat(path);
    options = options || this.defaultRequestOptions();

    return this.request('PUT', url, body, options, true, rethrowError);
  }

  public delete(path: string, options?: object, rethrowError: boolean = false): Observable<any> {
    const url = this.appConfigService.baseApiUrl.concat(path);
    options = options || this.defaultRequestOptions();

    return this.request('DELETE', url, null, options, true, rethrowError);
  }

  private request(
    method: string,
    url: string,
    body: any = undefined,
    options: any = undefined,
    ok404: boolean = false,
    rethrowError: boolean = false
  ): Observable<any> {
    let params = this.setParams(options);
    let headers = this.setHeaders(options);
    let request = new HttpRequest(method, url, body, { headers: headers, params: params });
    return this.http
      .request(request)
      .pipe(map(this.extractData))
      .pipe(catchError((err, caught) => this.handleError(err, ok404, rethrowError)))
      .pipe(publishLast())
      .pipe(refCount());
  }

  private setParams(options: any): HttpParams {
    let params = new HttpParams();

    if (options && options.params) {
      _.each(options.params, function (value, key) {
        params = params.append(key, value);
      });
    }

    return params;
  }

  private setHeaders(options: any): HttpHeaders {
    return options && options.headers ? options.headers : new HttpHeaders();
  }

  public extractData(res) {
    if (_.isNil(res) || !res.ok) {
      return {};
    }

    return res.body;
  }

  public defaultRequestOptions() {
    return {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };
  }

  public openSnackBar(message: string, success: boolean = false) {
    this.snackbarService.open(message, success);
  }
}
